import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  LicenseAuditEntry, LicenseIntent, LicenseRejectReason, LicenseType, LicenseUploadResult, PendingLicense, ReusableLicense,
} from '../models/license';
import { FeatureFlagsService } from './featureFlags.service';

const LICENSE_API = `${environment.api_url_v1}/services/document/document/license`;

@Injectable({ providedIn: 'root' })
export class LicenseDocumentService {
  constructor(private http: HttpClient, private featureFlagsService: FeatureFlagsService) {}

  /** Benev-style tenants verify loosely: state-only address check, board
   *  verification sheets accepted, number changes auto-apply
   *  (process.allow_state_only_license_match). */
  isRelaxedTenant(): boolean {
    return !!this.featureFlagsService.getByEnv('process', 'allow_state_only_license_match');
  }

  /** Whether the tenant lets customers/reps edit a facility's address (Benev only
   *  today) — the same flags that show Facilities' "Edit Facility Info".
   *  ponytail: either flag, not per role; both are set per tenant together. */
  canEditFacilityAddress(): boolean {
    return !!(this.featureFlagsService.getByEnv('process', 'allow_customer_facility_edit')
      || this.featureFlagsService.getByEnv('process', 'allow_rep_facility_edit'));
  }


  upload(request: {
    file: File;
    facilityId: number;
    companyId: number;
    licenseType: LicenseType;
    intent: LicenseIntent;
    newLicenseNumber?: string;
    replacesDocumentId?: number | null;
  }): Observable<LicenseUploadResult> {
    const params = {
      context: 'facility',
      type: request.licenseType,
      facility_id: request.facilityId,
      company_id: request.companyId,
      intent: request.intent,
      new_license_number: request.newLicenseNumber || undefined,
      replaces_document_id: request.replacesDocumentId || undefined,
      client: environment.supplierConfig.clientName,
    };
    const form = new FormData();
    form.append('params', JSON.stringify(params));
    form.append('doc', request.file, request.file.name.replace(/[^\w.-]+/g, '_'));
    return this.http
      .post<{ data: LicenseUploadResult }>(`${environment.api_url_v1}/services/document/document/upload`, form)
      .pipe(map(response => response.data));
  }

  change(documentId: number, newLicenseNumber?: string): Observable<LicenseUploadResult> {
    return this.http
      .post<{ data: LicenseUploadResult }>(`${LICENSE_API}/${documentId}/change`,
        newLicenseNumber ? { new_license_number: newLicenseNumber } : {})
      .pipe(map(response => response.data));
  }

  submit(documentId: number): Observable<unknown> {
    return this.http.post(`${LICENSE_API}/${documentId}/submit`, {});
  }

  /** Drop an upload that hasn't been sent for review. */
  withdraw(documentId: number): Observable<unknown> {
    return this.http.post(`${LICENSE_API}/${documentId}/withdraw`, {});
  }

  /** A verified copy of this number already held by the facility's own company. */
  reusable(facilityId: number, number: string): Observable<ReusableLicense> {
    return this.http
      .get<{ data: ReusableLicense }>(`${LICENSE_API}/reusable`, { params: { facility_id: facilityId, number } })
      .pipe(map(response => response.data));
  }

  reuse(facilityId: number, number: string): Observable<LicenseUploadResult> {
    return this.http
      .post<{ data: LicenseUploadResult }>(`${LICENSE_API}/reuse`, { facility_id: facilityId, number })
      .pipe(map(response => response.data));
  }

  pending(facilityId: number): Observable<PendingLicense[]> {
    return this.http
      .get<{ data: PendingLicense[] }>(`${LICENSE_API}/pending`, { params: { facility_id: facilityId } })
      .pipe(map(response => response.data ?? []));
  }

  approve(documentId: number, body: { expiration: string; license_number?: string }): Observable<unknown> {
    return this.http.post(`${LICENSE_API}/${documentId}/approve`, body);
  }

  reject(documentId: number, body: { reason: LicenseRejectReason; note?: string }): Observable<unknown> {
    return this.http.post(`${LICENSE_API}/${documentId}/reject`, body);
  }

  /** Clear a stale upload; nobody is emailed. */
  dismiss(documentId: number): Observable<unknown> {
    return this.http.post(`${LICENSE_API}/${documentId}/dismiss`, {});
  }

  /** Admin only: every licence event for the facility, newest first. */
  auditTrail(facilityId: number): Observable<LicenseAuditEntry[]> {
    return this.http
      .get<{ data: LicenseAuditEntry[] }>(`${LICENSE_API}/audit`, { params: { facility_id: facilityId } })
      .pipe(map(response => response.data ?? []));
  }
}
